import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import "./WhatsAppChat.css";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WhatsAppService from "../../services/WhatsAppServices";
import { WhatsAppMsgStatus } from "../../constants/whatsAppConstants";
import { useWhatsAppMessage } from "../../context/WhatsAppMessageContext";

const API_THROTTLE_TIME = 5 * 60 * 1000; // 5 minutes in milliseconds
const MIN_SEARCH_LENGTH = 3;

const getSearchQuery = (value) => {
  const trimmed = (value || "").trim();
  if (!trimmed || trimmed.length < MIN_SEARCH_LENGTH) return undefined;
  return trimmed;
};

const isSearchTooShort = (value) => {
  const trimmed = (value || "").trim();
  return trimmed.length > 0 && trimmed.length < MIN_SEARCH_LENGTH;
};

/** Renders plain or WhatsApp-style template bubbles (header + body + action rows). */
function MessageBubbleContent({ message: m }) {
  const tpl = useMemo(() => parseTemplateFromMessage(m), [m]);

  if (tpl.header || tpl.buttons.length > 0) {
    return (
      <div className="wa-msg-template">
        {tpl.header ? (
          <div className="wa-template-header">{tpl.header}</div>
        ) : null}
        <div className="wa-template-body">{renderMultilineText(tpl.body)}</div>
        {tpl.buttons.length > 0 ? (
          <div className="wa-template-actions" role="group">
            {tpl.buttons.map((b, i) => (
              <TemplateActionButton key={i} action={b} />
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <span className="wa-msg-plain">{linkifyPlainMessage(m.text)}</span>
  );
}

function TemplateActionButton({ action }) {
  const href =
    action.type === "url"
      ? action.url
      : action.type === "phone"
        ? toTelHref(action.phone)
        : null;
  if (!href) return null;

  const isUrl = action.type === "url";
  return (
    <a
      className="wa-template-btn"
      href={href}
      target={isUrl ? "_blank" : undefined}
      rel={isUrl ? "noopener noreferrer" : undefined}
    >
      <span className="wa-template-btn-icon" aria-hidden>
        {isUrl ? <LinkGlyph /> : <PhoneGlyph />}
      </span>
      <span className="wa-template-btn-label">{action.title}</span>
    </a>
  );
}

function LinkGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 0 1-7-7l1-1"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PhoneGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// Persist throttle timestamps and cached data across component unmounts
// (e.g. when switching WhatsApp tabs).
let lastLeadsCallTs = 0;
let lastMessagesCallTs = 0;

let cachedChats = [];
let cachedSelectedId = null;
let cachedLeadPage = 1;
let cachedHasMoreLeads = true;
let cachedMsgPage = 1;
let cachedHasMoreMessages = true;
let cachedQuery = "";

export default function WhatsAppChat() {
  const [chats, setChats] = useState(() => cachedChats);
  const [selectedId, setSelectedId] = useState(() => cachedSelectedId);
  const [input, setInput] = useState("");
  const [query, setQuery] = useState(() => cachedQuery);

  const [leadPage, setLeadPage] = useState(() => cachedLeadPage || 1);
  const [hasMoreLeads, setHasMoreLeads] = useState(
    () => cachedHasMoreLeads
  );

  const [msgPage, setMsgPage] = useState(() => cachedMsgPage || 1);
  const [hasMoreMessages, setHasMoreMessages] = useState(
    () => cachedHasMoreMessages
  );
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const messagesRef = useRef(null);
  const searchTimeout = useRef(null);
  const scrollHeightRef = useRef(0);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef(null);
  // Throttling refs to track last API call times
  const lastLeadsCall = useRef(lastLeadsCallTs);
  const lastMessagesCall = useRef(lastMessagesCallTs);
  const { registerMessageHandler, unregisterMessageHandler, setUnreadCount, resetUnreadCount } = useWhatsAppMessage();

  /* ---------------- RESET UNREAD COUNT ON MOUNT (Entering WhatsApp Page) ---------------- */
  useEffect(() => {
    resetUnreadCount();
  }, [resetUnreadCount]);

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    loadLeads(1, "");
  }, []);

  /* ---------------- BACKEND SEARCH ---------------- */
  useEffect(() => {
    clearTimeout(searchTimeout.current);

    if (isSearchTooShort(query)) {
      return;
    }

    searchTimeout.current = setTimeout(() => {
      setLeadPage(1);
      setHasMoreLeads(true);
      // For search we always force a fresh API call, even within throttle window
      loadLeads(1, getSearchQuery(query) ?? "", true);
    }, 400);

    return () => clearTimeout(searchTimeout.current);
  }, [query]);

  /* ---------------- LOAD LEADS ---------------- */
  const loadLeads = useCallback(
    async (page, search, force = false) => {
      const now = Date.now();
      if (!force && (now - lastLeadsCall.current) < API_THROTTLE_TIME) {
        console.log("Leads API call throttled - last call was less than 5 minutes ago");
        return;
      }

      try {
        lastLeadsCall.current = now;
        lastLeadsCallTs = now;
        const res = await WhatsAppService.getAllLeads({
          PageNumber: page,
          PageSize: 10,
          Search: getSearchQuery(search) ?? "",
        });

        const items = res?.data?.data?.items || [];

        const mapped = items.map((item) => {
          const rawLatestMessages =
            item.latestMessages ||
            (item.latestMessage ? [item.latestMessage] : []);

          const latestMessages = rawLatestMessages
            .filter(Boolean)
            .map((msg) => ({
              id: msg.id,
              sender: msg.direction === 2 ? "me" : "other",
              text: msg.messageBody,
              messageHeader: msg.messageHeader ?? msg.header,
              buttons: msg.buttons ?? msg.templateButtons,
              time: msg.timestamp,
              ts: formatTime(msg.timestamp),
              status: msg.status,
            }))
            .sort((a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time));

          const latest = latestMessages[latestMessages.length - 1];

          return {
            id: item.leadId,
            name: item.leadName,
            phoneNumber: item.phoneNumber,
            unread: item.unreadCount || 0,

            lastMessageText: latest?.text || "",
            lastMessageTime: latest?.time || null,
            lastMessageStatus: latest?.status,

            messages: latestMessages.slice(-5),
          };
        });

        setChats((prev) => {
          const updatedChats =
            page === 1 ? sortChats(mapped) : sortChats([...prev, ...mapped]);

          // Update global unread count with total from all chats
          const totalUnread = updatedChats.reduce(
            (sum, chat) => sum + (chat.unread || 0),
            0
          );
          setUnreadCount(totalUnread);

          // Update cache with latest chats
          cachedChats = updatedChats;
          return updatedChats;
        });

        const moreLeads = items.length === 10;
        setHasMoreLeads(moreLeads);
        setLeadPage(page);

        cachedLeadPage = page;
        cachedHasMoreLeads = moreLeads;
        cachedQuery = search;

        // Handle selection when page 1 (new search or initial load)
        if (page === 1) {
          setSelectedId((prevSelected) => {
            // Check if the previously selected chat still exists in the new results
            const selectedChatExists = mapped.some((chat) => chat.id === prevSelected);

            if (mapped.length > 0) {
              // If previous selection exists, keep it; otherwise select first result
              const nextSelected = selectedChatExists ? prevSelected : mapped[0].id;
              cachedSelectedId = nextSelected;
              return nextSelected;
            } else {
              // No results - clear selection
              cachedSelectedId = null;
              return null;
            }
          });
        }
      } catch (err) {
        console.error("Failed to load leads", err);
      }
    },
    [setUnreadCount]
  );

  /* ---------------- LOAD MESSAGES ---------------- */
  const loadMessages = useCallback(async (leadId, page = 1, force = false) => {
    if (!leadId || loadingMessages) return;

    const container = messagesRef.current;
    if (!container) return;

    // Throttle only for page 1 loads (initial load), not for pagination
    if (page === 1) {
      const now = Date.now();
      if (!force && (now - lastMessagesCall.current) < API_THROTTLE_TIME) {
        console.log("Messages API call throttled - last call was less than 5 minutes ago");
        return;
      }
      lastMessagesCall.current = now;
      lastMessagesCallTs = now;
    }

    // Save scroll position before loading (for pagination)
    const wasAtTop = page > 1;
    const previousScrollHeight = wasAtTop ? container.scrollHeight : 0;
    const previousScrollTop = wasAtTop ? container.scrollTop : 0;

    try {
      setLoadingMessages(true);

      const res = await WhatsAppService.getLeadById(leadId, {
        PageNumber: page,
        PageSize: 50,
      });

      const items = res?.data?.data?.items || [];

      const newMessages = items.map((m) => ({
        id: m.waMessageId,
        sender: m.direction === 2 ? "me" : "other",
        text: m.messageBody,
        messageHeader: m.messageHeader ?? m.header,
        buttons: m.buttons ?? m.templateButtons,
        time: m.timestamp,
        ts: formatTime(m.timestamp),
        status: m.status,
      }));

      // Sort messages by timestamp in ascending order (oldest first, newest last)
      const sortedNewMessages = [...newMessages].sort(
        (a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time)
      );

      setChats((prev) => {
        const updatedChats = prev.map((c) => {
          if (c.id !== leadId) return c;

          let updatedMessages;
          if (page === 1) {
            // First page: use sorted messages
            updatedMessages = sortedNewMessages;
          } else {
            // Subsequent pages: prepend older messages and sort the entire array
            updatedMessages = [...sortedNewMessages, ...c.messages].sort(
              (a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time)
            );
          }

          return {
            ...c,
            messages: updatedMessages,
            unread: 0, // Mark as read when loading messages
            lastMessageText:
              page === 1
                ? updatedMessages[updatedMessages.length - 1]?.text || c.lastMessageText
                : c.lastMessageText,
            lastMessageTime:
              page === 1
                ? updatedMessages[updatedMessages.length - 1]?.time || c.lastMessageTime
                : c.lastMessageTime,
          };
        });

        // Update global unread count
        const totalUnread = updatedChats.reduce((sum, chat) => sum + (chat.unread || 0), 0);
        setUnreadCount(totalUnread);

        // Update cache with latest chats
        cachedChats = updatedChats;

        return updatedChats;
      });

      const moreMessages = items.length === 50;
      setHasMoreMessages(moreMessages);
      setMsgPage(page);

      cachedMsgPage = page;
      cachedHasMoreMessages = moreMessages;

      // Restore scroll position after DOM update
      if (wasAtTop && page > 1) {
        // Use double requestAnimationFrame to ensure DOM has fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const currentContainer = messagesRef.current;
            if (currentContainer) {
              const newScrollHeight = currentContainer.scrollHeight;
              const heightDifference = newScrollHeight - previousScrollHeight;
              // Maintain scroll position relative to the content that was visible
              currentContainer.scrollTop = previousScrollTop + heightDifference;
            }
          });
        });
      }
    } catch (err) {
      console.error("Failed to load messages", err);
    } finally {
      setLoadingMessages(false);
    }
  }, [loadingMessages, setUnreadCount]);

  /* ---------------- AUTO-SELECT FIRST CHAT IF NONE SELECTED ---------------- */
  useEffect(() => {
    // If we have chats but no selected chat, select the first one
    if (chats.length > 0 && !selectedId) {
      const firstChatId = chats[0].id;
      setSelectedId(firstChatId);
      cachedSelectedId = firstChatId;

      // Use the latestMessages preview for the top lead if available.
      // Only fetch the full chat when no preview messages are present.
      if (!chats[0].messages || chats[0].messages.length === 0) {
        loadMessages(firstChatId, 1);
      }
    }
  }, [chats, selectedId, loadMessages]);

  /* ---------------- SCROLL HANDLER ---------------- */
  const handleMessageScroll = useCallback(() => {
    const container = messagesRef.current;
    if (!container || loadingMessages || !hasMoreMessages || isScrollingRef.current) return;

    // Throttle scroll events
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!container) return;

      // Use a threshold (100px from top) instead of exact 0 for better UX
      const scrollThreshold = 100;
      const isNearTop = container.scrollTop < scrollThreshold;

      if (isNearTop && !isScrollingRef.current) {
        isScrollingRef.current = true;
        loadMessages(selectedId, msgPage + 1).finally(() => {
          // Reset flag after a short delay to prevent rapid firing
          setTimeout(() => {
            isScrollingRef.current = false;
          }, 500);
        });
      }
    }, 100); // Throttle to 100ms
  }, [selectedId, msgPage, hasMoreMessages, loadingMessages, loadMessages]);

  /* ---------------- SEND MESSAGE ---------------- */
  const selectedChat = chats.find((c) => c.id === selectedId);

  const sendMessage = async () => {
    if (!input.trim() || !selectedChat) return;

    const tempId = Date.now();
    const now = new Date().toISOString();

    const tempMsg = {
      id: tempId,
      sender: "me",
      text: input,
      time: now,
      ts: formatTime(now),
      status: WhatsAppMsgStatus.Sent,
    };

    setChats((prev) =>
      sortChats(
        prev.map((c) => {
          if (c.id !== selectedId) return c;

          // Add new message and ensure chronological order
          const updatedMessages = [...c.messages, tempMsg].sort(
            (a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time)
          );

          return {
            ...c,
            messages: updatedMessages,
            lastMessageText: tempMsg.text,
            lastMessageTime: now,
            lastMessageStatus: WhatsAppMsgStatus.Sent,
          };
        })
      )
    );

    setInput("");

    try {
      await WhatsAppService.sendMessage({
        leadId: selectedChat.id,
        phoneNumber: selectedChat.phoneNumber,
        messageBody: tempMsg.text,
      });

      updateMessageStatus(tempId, WhatsAppMsgStatus.Delivered);
    } catch (err) {
      updateMessageStatus(tempId, WhatsAppMsgStatus.Failed);
      toast.error(err?.response?.data?.message || "Failed to send message");
    }
  };

  const updateMessageStatus = (msgId, status) => {
    setChats((prev) => {
      const nextChats = prev.map((c) =>
        c.id === selectedId
          ? {
            ...c,
            messages: c.messages.map((m) =>
              m.id === msgId ? { ...m, status } : m
            ),
            lastMessageStatus: status,
          }
          : c
      );
      cachedChats = nextChats;
      return nextChats;
    });
  };

  /* ---------------- HANDLE NEW MESSAGE FROM SIGNALR ---------------- */
  const handleNewMessage = useCallback((msg) => {
    // Extract leadId from message - check multiple possible property names
    const leadId = msg.leadId;
    const messageText = msg.message || msg.messageBody || "";
    const timestamp = msg.timestamp || new Date().toISOString();
    const direction = msg.direction || 1; // 1 = incoming, 2 = outgoing
    const messageId = msg.waMessageId || msg.id || `temp-${Date.now()}`;

    if (!leadId && !msg.leadName && !msg.phoneNumber) {
      console.warn("Received message without lead identifier", msg);
      return;
    }

    setChats((prev) => {
      // Find the chat by leadId, or by name/phone if leadId is not available
      const chatIndex = prev.findIndex(
        (c) =>
          (leadId && c.id === leadId) ||
          (!leadId && msg.leadName && c.name === msg.leadName) ||
          (!leadId && msg.phoneNumber && c.phoneNumber === msg.phoneNumber)
      );

      if (chatIndex === -1) {
        // Chat not found - might be a new lead, reload leads to get it
        console.log("Chat not found for message, reloading leads...");
        loadLeads(1, "", true); // Force refresh for new messages
        return prev;
      }

      const chat = prev[chatIndex];
      const isSelected = chat.id === selectedId;
      const isIncoming = direction !== 2;

      // Create new message object
      const newMessage = {
        id: messageId,
        sender: isIncoming ? "other" : "me",
        text: messageText,
        messageHeader: msg.messageHeader ?? msg.header,
        buttons: msg.buttons ?? msg.templateButtons,
        time: timestamp,
        ts: formatTime(timestamp),
        status: isIncoming ? WhatsAppMsgStatus.Received : WhatsAppMsgStatus.Sent,
      };

      // Update chat
      let updatedMessages = chat.messages;
      if (isSelected) {
        // Add new message and sort to maintain chronological order
        updatedMessages = [...chat.messages, newMessage].sort(
          (a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time)
        );
      }

      const updatedChat = {
        ...chat,
        lastMessageText: messageText,
        lastMessageTime: timestamp,
        lastMessageStatus: newMessage.status,
        // Increase unread count only if it's an incoming message and chat is not selected
        unread: isIncoming && !isSelected ? (chat.unread || 0) + 1 : chat.unread,
        // Add message to messages array if chat is selected, otherwise just update preview
        messages: updatedMessages,
      };

      // Create new array with updated chat, sorted by last message time
      const updatedChats = [...prev];
      updatedChats[chatIndex] = updatedChat;

      // Sort chats by last message time
      const sortedChats = sortChats(updatedChats);

      // Update global unread count
      const totalUnread = sortedChats.reduce((sum, chat) => sum + (chat.unread || 0), 0);
      setUnreadCount(totalUnread);

      // Update cache
      cachedChats = sortedChats;

      return sortedChats;
    });

    // Auto-scroll to bottom if this chat is selected and user is near bottom
    if (messagesRef.current && selectedId && selectedId === leadId) {
      const container = messagesRef.current;
      const isNearBottom =
        container.scrollHeight - container.scrollTop - container.clientHeight < 100;

      if (isNearBottom) {
        setTimeout(() => {
          if (messagesRef.current) {
            messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
          }
        }, 100);
      }
    }
  }, [selectedId, loadLeads, setUnreadCount]);

  /* ---------------- REGISTER MESSAGE HANDLER ---------------- */
  useEffect(() => {
    registerMessageHandler(handleNewMessage);
    return () => {
      unregisterMessageHandler();
    };
  }, [handleNewMessage, registerMessageHandler, unregisterMessageHandler]);

  /* ---------------- CLEANUP SCROLL TIMEOUT ---------------- */
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  /* ---------------- RESET SCROLL ON CHAT CHANGE OR INITIAL LOAD ---------------- */
  useEffect(() => {
    const container = messagesRef.current;
    if (!container || !selectedChat) return;

    // Only auto-scroll to bottom on initial load or when chat changes
    if (isInitialLoad || !selectedChat.messages.length) {
      // Use setTimeout to ensure DOM has rendered
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
          setIsInitialLoad(false);
        }
      }, 100);
    }
  }, [selectedId, isInitialLoad]);

  /* ---------------- SCROLL TO BOTTOM WHEN NEW MESSAGES ARRIVE (if at bottom) ---------------- */
  useEffect(() => {
    const container = messagesRef.current;
    if (!container || !selectedChat) return;

    // Only auto-scroll if user is near the bottom (within 100px)
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight < 100;

    if (isNearBottom && selectedChat.messages.length > 0) {
      setTimeout(() => {
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      }, 50);
    }
  }, [selectedChat, selectedChat?.messages.length]);

  /* ---------------- HELPERS ---------------- */
  const TextAvatar = ({ name }) => (
    <div className="wa-text-avatar">{name?.charAt(0)}</div>
  );

  const groupedMessages = useMemo(() => {
    if (!selectedChat) return [];

    // Ensure messages are sorted chronologically (oldest first, newest last)
    const sortedMessages = [...selectedChat.messages].sort(
      (a, b) => parseUtcTimestamp(a.time) - parseUtcTimestamp(b.time)
    );

    return Object.entries(
      sortedMessages.reduce((acc, msg) => {
        const key = getDayLabel(msg.time);
        acc[key] = acc[key] || [];
        acc[key].push(msg);
        return acc;
      }, {})
    );
  }, [selectedChat]);

  /* ---------------- UI ---------------- */
  return (
    <div className="wa-app">
      {/* SIDEBAR */}
      <aside className="wa-sidebar">
        <div className="wa-title">WhatsApp</div>

        <input
          className="wa-search"
          placeholder="Search name or number (min 3 characters)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {isSearchTooShort(query) ? (
          <div className="wa-search-hint">
            Enter at least {MIN_SEARCH_LENGTH} characters to search.
          </div>
        ) : null}

        <div className="wa-chat-list">
          {chats.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p>No chats found</p>
            </div>
          ) : (
            chats.map((c) => (
              <div
                key={c.id}
                className={`wa-chat-item ${c.id === selectedId ? "active" : ""
                  }`}
                onClick={() => {
                  setSelectedId(c.id);
                  cachedSelectedId = c.id;
                  setMsgPage(1);
                  setHasMoreMessages(true);
                  cachedMsgPage = 1;
                  cachedHasMoreMessages = true;
                  setIsInitialLoad(true);

                  // clear messages immediately
                  setChats((prev) =>
                    prev.map((chat) =>
                      chat.id === c.id
                        ? { ...chat, messages: [] }
                        : chat
                    )
                  );

                  loadMessages(c.id, 1, true);
                }}
              >
                <TextAvatar name={c.name} />

                <div className="chat-info">
                  <div className="chat-row">
                    <span className="name">{c.name}</span>

                    <div className="right-meta">
                      <span className="time">
                        {c.lastMessageTime
                          ? formatTime(c.lastMessageTime)
                          : ""}
                      </span>
                      {c.unread > 0 && (
                        <span className="unread-badge">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="preview">
                    {c.lastMessageStatus && (
                      <span
                        className={`sidebar-ticks ${c.lastMessageStatus}`}
                      >
                        {c.lastMessageStatus === WhatsAppMsgStatus.Sent && "✓"}
                        {(c.lastMessageStatus === WhatsAppMsgStatus.Delivered ||
                          c.lastMessageStatus === WhatsAppMsgStatus.Read) &&
                          "✓✓"}
                      </span>
                    )}
                    <span>{c.lastMessageText}</span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* CHAT */}
      <main className="wa-chat">
        {selectedChat ? (
          <>
            <header className="wa-header">
              <TextAvatar name={selectedChat.name} />
              <div className="name">{selectedChat.name}</div>
            </header>

            <div
              key={selectedId}
              className="wa-messages"
              ref={messagesRef}
              onScroll={handleMessageScroll}
            >
              {loadingMessages && (
                <div className="loading">Loading…</div>
              )}

              {groupedMessages.map(([day, msgs]) => (
                <React.Fragment key={day}>
                  <div className="day-separator">{day}</div>
                  {msgs.map((m) => (
                    <div key={m.id} className={`wa-msg ${m.sender}`}>
                      <MessageBubbleContent message={m} />
                      <div className="msg-meta">
                        <small>{m.ts}</small>
                        {m.sender === "me" && (
                          <span className={`ticks ${m.status}`}>
                            {m.status === WhatsAppMsgStatus.Sent && "✓"}
                            {(m.status === WhatsAppMsgStatus.Delivered ||
                              m.status === WhatsAppMsgStatus.Read) &&
                              "✓✓"}
                            {m.status === WhatsAppMsgStatus.Failed && "⚠️"}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </React.Fragment>
              ))}
            </div>

            <footer className="wa-input">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                placeholder="Type a message"
              />
              <button onClick={sendMessage}>Send</button>
            </footer>
          </>
        ) : (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#666'
          }}>
            <p>{chats.length === 0 ? 'No chats found. Try searching.' : 'Select a chat to start messaging'}</p>
          </div>
        )}
      </main>
    </div>
  );
}

/* ---------------- UTILITIES ---------------- */
const parseUtcTimestamp = (value) => {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === "number") return new Date(value);

  const str = String(value).trim();
  if (!str) return new Date(NaN);

  if (/[zZ]$/.test(str) || /[+-]\d{2}:\d{2}$/.test(str)) {
    return new Date(str);
  }

  if (/^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/.test(str)) {
    const normalized = str.replace(" ", "T");
    return new Date(normalized.endsWith("Z") ? normalized : `${normalized}Z`);
  }

  return new Date(str);
};

const formatTime = (date) =>
  parseUtcTimestamp(date).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

const sortChats = (arr) =>
  [...arr].sort(
    (a, b) =>
      parseUtcTimestamp(b.lastMessageTime || 0).getTime() -
      parseUtcTimestamp(a.lastMessageTime || 0).getTime()
  );

const getDayLabel = (date) => {
  const d = parseUtcTimestamp(date);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return "Today";
  if (d.toDateString() === yesterday.toDateString())
    return "Yesterday";

  return d.toLocaleDateString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

/* ---------------- TEMPLATE MESSAGE PARSING / RENDER ---------------- */

function defaultButtonTitleForUrl(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./i, "");
    return host || "Open link";
  } catch {
    return "Open link";
  }
}

function normalizeButtonsFromApi(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const b of arr) {
    const typeRaw = (b.type || b.buttonType || "")
      .toString()
      .toLowerCase()
      .replace(/-/g, "_");
    const url = (b.url || "").trim();
    const phone = (
      b.phone ??
      b.phoneNumber ??
      b.phone_number ??
      ""
    )
      .toString()
      .trim();
    let title = (b.title || b.text || "").trim();

    const isUrl =
      typeRaw === "url" || (!!url && /^https?:\/\//i.test(url));
    const isPhone =
      typeRaw === "phone" ||
      typeRaw === "phone_number" ||
      (!!phone && !isUrl);

    if (isUrl && /^https?:\/\//i.test(url)) {
      if (!title) title = defaultButtonTitleForUrl(url);
      out.push({ type: "url", title, url });
      continue;
    }
    if (isPhone && phone) {
      if (!title) title = "Call";
      out.push({ type: "phone", title, phone });
    }
  }
  return out;
}

function parseBracketActionLine(line) {
  const trimmed = line.trim();
  const m = trimmed.match(/^\[.*?\s*([^:]+):\s*(.+)\]$/);
  if (!m) return null;
  const title = m[1].trim();
  const value = m[2].trim();
  if (/^https?:\/\//i.test(value))
    return { type: "url", title, url: value };
  const digits = value.replace(/[\s\-]/g, "");
  if (/^\+?\d{8,}$/.test(digits))
    return { type: "phone", title, phone: digits.startsWith("+") ? digits : `+${digits}` };
  return null;
}

function parseFlatBodyIntoTemplate(raw) {
  const text = raw.replace(/\r\n/g, "\n").trim();
  const segments = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (!segments.length)
    return { header: null, body: "", buttons: [] };

  const tailButtons = [];
  let end = segments.length;
  while (end > 0) {
    const seg = segments[end - 1];
    const lines = seg.split("\n").map((l) => l.trim()).filter(Boolean);
    if (!lines.length) {
      end--;
      continue;
    }
    const parsed = lines.map(parseBracketActionLine);
    if (parsed.every(Boolean)) {
      tailButtons.unshift(...parsed);
      end--;
    } else break;
  }

  if (!tailButtons.length)
    return { header: null, body: text, buttons: [] };

  let header = null;
  let start = 0;
  if (end >= 1) {
    const first = segments[0];
    if (!first.includes("\n") && first.length <= 120) {
      header = first;
      start = 1;
    }
  }

  const body = segments.slice(start, end).join("\n\n");
  return { header, body, buttons: tailButtons };
}

/** When API sends buttons but no header, treat first short paragraph as header (WhatsApp template style). */
function splitOptionalHeaderFromBody(bodyText) {
  const text = (bodyText || "").replace(/\r\n/g, "\n").trim();
  const segments = text.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);
  if (segments.length < 2) return { header: null, body: text };
  const first = segments[0];
  if (!first.includes("\n") && first.length <= 120) {
    return { header: first, body: segments.slice(1).join("\n\n") };
  }
  return { header: null, body: text };
}

function parseTemplateFromMessage(msg) {
  const apiButtons = normalizeButtonsFromApi(msg.buttons);
  let apiHeader = msg.messageHeader?.trim?.() || null;
  let body = (msg.text || "").trim();

  if (apiHeader || apiButtons.length) {
    if (!apiHeader && apiButtons.length) {
      const split = splitOptionalHeaderFromBody(body);
      apiHeader = split.header;
      body = split.body;
    }
    return {
      header: apiHeader,
      body,
      buttons: apiButtons,
    };
  }
  return parseFlatBodyIntoTemplate(msg.text || "");
}

function toTelHref(phone) {
  const d = phone.replace(/[^\d+]/g, "");
  if (!d) return "#";
  return `tel:${d}`;
}

function renderMultilineText(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 ? <br /> : null}
      {line}
    </React.Fragment>
  ));
}

const URL_IN_TEXT = /(https?:\/\/[^\s<]+[^.,;:!?)'\s])/gi;

function linkifyPlainMessage(text) {
  if (!text) return null;
  const lines = text.split("\n");
  return lines.map((line, li) => (
    <div key={li}>
      {line.split(URL_IN_TEXT).map((part, pi) => {
        if (/^https?:\/\//i.test(part)) {
          return (
            <a
              key={pi}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              className="wa-inline-link"
            >
              {part}
            </a>
          );
        }
        return <React.Fragment key={pi}>{part}</React.Fragment>;
      })}
    </div>
  ));
}
